/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Readiness — the one projection every console surface renders (R2/R2a, #897).
 *
 * Readiness answers **"will this work"**, and only ever from what the product has
 * actually observed. It replaces `deriveHealthState`, which answered a different
 * question badly: it returned "Healthy" for anything enabled with a non-empty URL,
 * so the badge was a statement about a text field. The 08/09 captures show a source
 * reading Healthy and then Critical seconds later, with nothing persisted either
 * way — a claim the product could not stand behind and the operator could not act
 * on.
 *
 * Three rules hold here, and each one is why a function exists in this file rather
 * than a ternary in a page:
 *
 * 1. **Never derive a state from configuration.** `readiness` is absent until
 *    something is observed, and absence renders as Unverified. {@link resolveValidation}
 *    is the only place that decides that, so no surface can invent Proven.
 * 2. **Labels come from the ratified vocabulary**, `BADGE_STATES` in the design
 *    system — not from local wording. The list, the detail header and the wizard's
 *    save claim must say the same words about the same source, or the operator
 *    reasonably concludes they mean different things.
 * 3. **A proof keeps its history.** `lastProvenAt` survives demotion on purpose:
 *    "last filled 3 days ago, before the request body changed" is the first line of
 *    the next diagnosis. {@link validationDetail} is what puts it on screen.
 *
 * The server owns the state transitions (`applyValidationOnSave`,
 * `recordDemandSourceValidation`) and the fingerprint that expires a proof. Nothing
 * here recomputes them — a second implementation in the browser would drift from
 * what was actually persisted, which is the mistake #936 cleaned up on the wire.
 */

import type {
  DemandSource,
  DemandSourceValidation,
  DemandSourceValidationState,
  ValidationFilter,
  RequestAffectingGroup,
} from '../types';
import { timeAgo } from './format';

/** Tone maps onto the console's semantic tokens, matching `BADGE_STATES`. */
export type ValidationTone = 'success' | 'warning' | 'error' | 'neutral';

/**
 * The ratified vocabulary. Mirrors `BADGE_STATES` in
 * `.claude/skills/bidbrain-design/components/core/Badge.jsx` — extend that first.
 *
 * `partial` and `valuesMissing` deliberately read as sentences rather than status
 * words: "Answering, not filling" tells the operator what happened, where a single
 * word like "Warning" tells them only how to feel about it.
 */
export const VALIDATION_VOCABULARY: Record<
  DemandSourceValidationState,
  { label: string; tone: ValidationTone; meaning: string }
> = {
  unverified: {
    label: 'Unverified',
    tone: 'neutral',
    meaning: 'Nothing has been observed yet. Not a failure — an absence of evidence.',
  },
  unreachable: {
    label: 'Unreachable',
    tone: 'error',
    meaning: 'The request did not come back. Nothing about the mapping has been established.',
  },
  rejected: {
    label: 'Rejected',
    tone: 'error',
    meaning: 'The exchange answered with a refusal.',
  },
  partial: {
    label: 'Answering, not filling',
    tone: 'warning',
    meaning: 'The exchange answered correctly but returned no ad. A no-bid is not a configuration fault.',
  },
  valuesMissing: {
    label: 'Filling, values missing',
    tone: 'warning',
    meaning: 'Ads came back without what the optimizer consumes — bid, duration or media URL.',
  },
  proven: {
    label: 'Proven',
    tone: 'success',
    meaning: 'An ad came back with everything the optimizer needs.',
  },
  needsReproving: {
    label: 'Needs re-proving',
    tone: 'warning',
    meaning: 'It was proven, then what it sends or how it reads a response changed.',
  },
};

/** The state a source renders in. Absence is Unverified — never derived from config. */
export function resolveValidation(source: Pick<DemandSource, 'validation'>): DemandSourceValidation {
  const stored = source.validation;
  if (!stored || !stored.state || !(stored.state in VALIDATION_VOCABULARY)) return { state: 'unverified' };
  return stored;
}

/** Label + tone for a source, via {@link resolveValidation}. */
export function validationLabel(validation: DemandSourceValidation): { label: string; tone: ValidationTone } {
  const { label, tone } = VALIDATION_VOCABULARY[validation.state] ?? VALIDATION_VOCABULARY.unverified;
  return { label, tone };
}

const GROUP_LABELS: Record<RequestAffectingGroup, string> = {
  endpoint: 'the endpoint',
  headers: 'the request headers',
  params: 'the request parameters',
  ortbBody: 'the request body',
  version: 'the protocol version',
  prebid: 'the Prebid configuration',
  responseMapping: 'the response mapping',
  timeout: 'the timeout',
  floor: 'the bid floor',
  podCapability: 'pod support',
};

/** "the request body and the response mapping" — mirrors `describeDemotion`. */
export function describeDemotion(groups: RequestAffectingGroup[] | undefined): string {
  const labels = (groups ?? []).map((g) => GROUP_LABELS[g]).filter(Boolean);
  if (labels.length === 0) return 'the configuration';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/**
 * The subtitle under the badge — the evidence, in one line.
 *
 * A badge alone says what bidBrain believes; this says **why and when**, which is
 * what makes it checkable. Order matters: for a demoted source the change that
 * expired the proof leads, because that is the thing to act on, and the age of the
 * proof follows it. `undefined` means there is genuinely nothing to add.
 */
export function validationDetail(validation: DemandSourceValidation): string | undefined {
  const observed = validation.observedAt ? timeAgo(validation.observedAt) : undefined;
  const proven = validation.lastProvenAt ? timeAgo(validation.lastProvenAt) : undefined;

  if (validation.state === 'unverified') return 'Never tested';

  if (validation.state === 'needsReproving') {
    const changed = `${describeDemotion(validation.demotedBy)} changed`;
    return proven ? `Last filled ${proven}, then ${changed}` : `Proven earlier, then ${changed}`;
  }

  if (validation.state === 'proven') {
    return proven ? `Filled ${proven}` : observed ? `Filled ${observed}` : validation.summary;
  }

  // Every remaining state is an unproven observation. Lead with what was seen, and
  // keep the last proof visible when there is one — a source that filled last week
  // and is rejecting today is a different problem from one that never filled.
  const seen = observed ? `Tested ${observed}` : validation.summary;
  if (proven) return seen ? `${seen} · last filled ${proven}` : `Last filled ${proven}`;
  return seen;
}

/** Labels for `ValidationFilter`, in the order the select offers them. */
export const READINESS_FILTER_OPTIONS: { value: ValidationFilter; label: string }[] = [
  { value: '', label: 'All readiness' },
  { value: 'proven', label: 'Proven' },
  { value: 'needsReproving', label: 'Needs re-proving' },
  { value: 'neverFilled', label: 'Never filled' },
  { value: 'neverTested', label: 'Never tested' },
  { value: 'failing', label: 'Unreachable or rejected' },
];

export function matchesValidationFilter(source: Pick<DemandSource, 'validation'>, filter: ValidationFilter): boolean {
  if (!filter) return true;
  const validation = resolveValidation(source);
  switch (filter) {
    case 'proven':
      return validation.state === 'proven';
    case 'needsReproving':
      return validation.state === 'needsReproving';
    case 'neverFilled':
      // Deliberately `lastProvenAt`, not the state: a source demoted from Proven
      // *has* filled, and belongs outside this answer even though it isn't proven
      // right now.
      return !validation.lastProvenAt;
    case 'neverTested':
      return validation.state === 'unverified';
    case 'failing':
      return validation.state === 'unreachable' || validation.state === 'rejected';
    default:
      return true;
  }
}
